import { getBackendBaseUrl } from './apiUrls';

/**
 * API client for support-issued payment-method magic links (zero-login).
 */

const BACKEND_BASE = getBackendBaseUrl();

export const PAYMENT_SESSION_API_URL = `${BACKEND_BASE}/billing/payment-method-sessions`;

export const PAYMENT_SESSION_ERROR_MESSAGES = {
  consumed: 'This payment method has already been updated.',
  expired: 'This link has expired. Please email support@keyflo.ai to request a new payment link.',
  not_found: 'This link is invalid. Please email support@keyflo.ai to request a new payment link.',
  cancelled: 'This payment link is no longer active.',
  account_cancelled: 'This account is cancelled. A payment-method update link cannot reactivate it.',
  payment_setup_not_allowed: 'Payment setup is not required for your account.',
};

export function getPaymentSessionErrorMessage(err, fallback = 'Unable to load this payment link.') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;

  const apiMessage = err.apiMessage || err.message;
  const code = err.error || err.code;

  if (apiMessage && String(apiMessage).trim() && apiMessage !== code) {
    return String(apiMessage).trim();
  }
  if (code && PAYMENT_SESSION_ERROR_MESSAGES[code]) {
    return PAYMENT_SESSION_ERROR_MESSAGES[code];
  }
  if (code) return String(code);
  return fallback;
}

class PaymentSessionError extends Error {
  constructor({ status, error, apiMessage, message }) {
    super(message || apiMessage || error || 'Payment session error');
    this.name = 'PaymentSessionError';
    this.status = status;
    this.error = error;
    this.apiMessage = apiMessage;
  }
}

function throwPaymentSessionError(res, data) {
  const code = data?.error;
  const apiMessage = data?.message || (code && PAYMENT_SESSION_ERROR_MESSAGES[code]) || code;
  throw new PaymentSessionError({
    status: res.status,
    error: code,
    apiMessage,
    message: apiMessage,
  });
}

async function parsePaymentSessionResponse(res) {
  try {
    return await res.json();
  } catch {
    if (!res.ok) {
      throw new PaymentSessionError({
        status: res.status,
        error: 'request_failed',
        apiMessage: 'Unable to load this payment link.',
        message: 'Unable to load this payment link.',
      });
    }
    return {};
  }
}

export async function fetchPaymentMethodSession(token) {
  const res = await fetch(`${PAYMENT_SESSION_API_URL}/${token}`);
  const data = await parsePaymentSessionResponse(res);
  if (!res.ok) throwPaymentSessionError(res, data);
  return data;
}

export async function startPaymentMethodSession(token) {
  const res = await fetch(`${PAYMENT_SESSION_API_URL}/${token}/start`, { method: 'POST' });
  const data = await parsePaymentSessionResponse(res);
  if (!res.ok) throwPaymentSessionError(res, data);
  return data;
}

export function sessionShowsPaymentUpdated(session) {
  return session?.status === 'completed';
}

export function sessionShowsReactivationWarning(session) {
  return Boolean(session?.account_cancelled) && session?.status !== 'completed';
}
